import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  Certification,
  CertificationType,
  CertificationOwnerType,
  CertificationStatus
} from '../models/certification.model';

/**
 * Certification Create Request
 */
export interface CertificationCreateRequest {
  certificationType: CertificationType;
  ownerType: CertificationOwnerType;
  organizationId?: string;
  profileId?: string;
  certificateNumber: string;
  issuedDate: string;
  expirationDate?: string;
  issuingAuthority: string;
  state?: string;
  documentUrl?: string;
  documentFileName?: string;
  notes?: string;
  previousCertificationId?: string;
}

/**
 * Certification Update Request
 */
export interface CertificationUpdateRequest {
  certificateNumber?: string;
  issuedDate?: string;
  expirationDate?: string;
  issuingAuthority?: string;
  state?: string;
  documentUrl?: string;
  documentFileName?: string;
  notes?: string;
  status?: CertificationStatus;
}

/**
 * File Upload Response
 */
export interface FileUploadResponse {
  url: string;
  fileUrl: string;
}

/**
 * Certification Service
 * Handles certification CRUD operations and file uploads
 */
@Injectable({
  providedIn: 'root'
})
export class CertificationService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get certifications for an organization
   */
  async getOrganizationCertifications(organizationId: string): Promise<Certification[]> {
    const response = await firstValueFrom(
      this.http.get<{ certifications: Certification[] }>(
        `${this.apiUrl}/certifications/organization/${organizationId}`
      )
    );
    return response.certifications || [];
  }

  /**
   * Get certifications for current user's profile
   */
  async getProfileCertifications(): Promise<Certification[]> {
    const response = await firstValueFrom(
      this.http.get<{ certifications: Certification[] }>(
        `${this.apiUrl}/certifications/profile`
      )
    );
    return response.certifications || [];
  }

  /**
   * Get a specific certification by ID
   */
  async getCertification(id: string): Promise<Certification | null> {
    const response = await firstValueFrom(
      this.http.get<{ certification: Certification }>(
        `${this.apiUrl}/certifications/${id}`
      )
    );
    return response.certification || null;
  }

  /**
   * Create a new certification
   */
  async createCertification(data: CertificationCreateRequest): Promise<Certification> {
    const response = await firstValueFrom(
      this.http.post<{ certification: Certification }>(
        `${this.apiUrl}/certifications`,
        data
      )
    );
    return response.certification;
  }

  /**
   * Update a certification
   */
  async updateCertification(id: string, data: CertificationUpdateRequest): Promise<Certification> {
    const response = await firstValueFrom(
      this.http.put<{ certification: Certification }>(
        `${this.apiUrl}/certifications/${id}`,
        data
      )
    );
    return response.certification;
  }

  /**
   * Delete (archive) a certification
   */
  async deleteCertification(id: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.apiUrl}/certifications/${id}`)
    );
  }

  /**
   * Renew a certification
   */
  async renewCertification(id: string, data: CertificationCreateRequest): Promise<Certification> {
    const response = await firstValueFrom(
      this.http.post<{ certification: Certification }>(
        `${this.apiUrl}/certifications/${id}/renew`,
        data
      )
    );
    return response.certification;
  }

  /**
   * Get renewal history for a certification
   */
  async getCertificationHistory(id: string): Promise<Certification[]> {
    const response = await firstValueFrom(
      this.http.get<{ history: Certification[] }>(
        `${this.apiUrl}/certifications/${id}/history`
      )
    );
    return response.history || [];
  }

  /**
   * Get certification status for an organization
   */
  async getOrganizationCertificationStatus(organizationId: string): Promise<{
    hasAll: boolean;
    missing: CertificationType[];
  }> {
    const response = await firstValueFrom(
      this.http.get<{ hasAll: boolean; missing: CertificationType[] }>(
        `${this.apiUrl}/certifications/organization/${organizationId}/status`
      )
    );
    return response;
  }

  /**
   * Get a pre-signed URL for uploading a certification document
   */
  async getUploadUrl(filename: string): Promise<{ uploadUrl: string; fileKey: string }> {
    const response = await firstValueFrom(
      this.http.post<FileUploadResponse>(
        `${this.apiUrl}/file-uploads/certification`,
        { filename }
      )
    );
    return { uploadUrl: response.url, fileKey: response.fileUrl };
  }

  /**
   * Upload a file to S3 using a pre-signed URL
   */
  async uploadFile(signedUrl: string, file: File): Promise<void> {
    await fetch(signedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    });
  }

  /**
   * Upload a certification document
   * Combines getting the signed URL and uploading the file
   */
  async uploadCertificationDocument(file: File): Promise<{ url: string; fileName: string }> {
    // Get signed URL and S3 key
    const { uploadUrl, fileKey } = await this.getUploadUrl(file.name);

    // Upload file to S3
    await this.uploadFile(uploadUrl, file);

    return {
      url: fileKey,
      fileName: file.name
    };
  }

  /**
   * Get a pre-signed download URL for a certification document
   */
  async getDownloadUrl(certificationId: string): Promise<string> {
    const response = await firstValueFrom(
      this.http.get<{ downloadUrl: string }>(
        `${this.apiUrl}/certifications/${certificationId}/download-url`
      )
    );
    return response.downloadUrl;
  }
}
